import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Info, OctagonAlert, Send, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { authenticatedApiClient } from '@/lib/api/client';
import { useSession } from '@/lib/auth-client';
import { EXPORT_REASONS } from './reasons-data';
import type { ExportRealityCheckSnapshot, ExportRealitySignal, ReasonImportance, SummaryExtension } from './types';
interface RealityCheckSummaryProps {
  snapshot: ExportRealityCheckSnapshot;
  extension?: SummaryExtension;
  showTally?: boolean;
  onBack: () => void;
  onReset: () => void;
}
const SIGNAL_CONFIG: Record<ExportRealitySignal, {
  label: string;
  headline: string;
  body: string;
  border: string;
}> = {
  incomplete: {
    label: 'Nothing flagged yet',
    headline: "You haven't weighed in yet.",
    body: 'Go back and flag the reasons that ring true for your business. The value is in the honest thinking, not the score.',
    border: 'border-gray-400'
  },
  reconsider: {
    label: 'Worth reconsidering',
    headline: 'The case for exporting looks shaky right now.',
    body: "You flagged real gaps in whether exporting makes sense for your NZ brand at this stage. Before asking whether you're ready, get honest about whether you should. The better investment may be growing domestically, entering Australia first, or strengthening what you already have.",
    border: 'border-rose-500'
  },
  'not-ready': {
    label: 'Not ready yet',
    headline: "The ambition might be right, but the readiness isn't there yet.",
    body: "You flagged real execution gaps. Winning an export order you can't serve profitably from NZ — or that starves your domestic customers — does more harm than good. Worth closing these gaps before committing capital and relationships offshore.",
    border: 'border-amber-500'
  },
  'proceed-with-eyes-open': {
    label: 'Nothing blocking',
    headline: 'No dealbreakers came up.',
    body: 'You worked through the hard questions and nothing critical is flagged. Keep treating export as one option among several — including growing NZ and Australia — and keep testing your assumptions before committing heavily.',
    border: 'border-teal-500'
  }
};
function reasonTitle(id: string): string {
  return EXPORT_REASONS.find(reason => reason.id === id)?.title ?? id;
}
function reasonImportance(id: string): ReasonImportance {
  return EXPORT_REASONS.find(reason => reason.id === id)?.importance ?? 'moderate';
}
const IMPORTANCE_ICON_CONFIG: Record<ReasonImportance, {
  icon: typeof OctagonAlert;
  className: string;
}> = {
  critical: {
    icon: OctagonAlert,
    className: 'text-rose-500'
  },
  high: {
    icon: TriangleAlert,
    className: 'text-amber-500'
  },
  moderate: {
    icon: Info,
    className: 'text-blue-400'
  }
};
export function RealityCheckSummary(t0) {
  const $ = _c(51);
  const {
    snapshot,
    extension,
    showTally: t1,
    onBack,
    onReset
  } = t0;
  const showTally = t1 === undefined ? false : t1;
  const {
    summary,
    answers
  } = snapshot;
  const config = SIGNAL_CONFIG[summary.signal];
  let t2;
  if ($[0] !== extension || $[1] !== snapshot) {
    t2 = typeof extension === "function" ? extension({
      snapshot
    }) : extension;
    $[0] = extension;
    $[1] = snapshot;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const resolvedExtension = t2;
  let t3;
  if ($[3] !== config.border) {
    t3 = cn("border-l-2 pl-4", config.border);
    $[3] = config.border;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== config.label) {
    t4 = <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{config.label}</p>;
    $[5] = config.label;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== config.headline) {
    t5 = <h2 className="mt-1 text-2xl font-semibold text-gray-900">{config.headline}</h2>;
    $[7] = config.headline;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== config.body) {
    t6 = <p className="mt-2 text-[15px] leading-relaxed text-gray-700">{config.body}</p>;
    $[9] = config.body;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== showTally || $[17] !== summary.answeredCount || $[18] !== summary.applicableCount || $[19] !== summary.readyConcerns || $[20] !== summary.shouldConcerns || $[21] !== summary.totalReasons || $[22] !== summary.unsureCount) {
    t8 = showTally && <p className="mt-6 text-sm text-gray-600">You weighed {summary.answeredCount} of {summary.totalReasons} reasons —{" "}{summary.applicableCount} flagged as real ({summary.shouldConcerns} on the case,{" "}{summary.readyConcerns} on readiness), {summary.unsureCount} unsure.</p>;
    $[16] = showTally;
    $[17] = summary.answeredCount;
    $[18] = summary.applicableCount;
    $[19] = summary.readyConcerns;
    $[20] = summary.shouldConcerns;
    $[21] = summary.totalReasons;
    $[22] = summary.unsureCount;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== summary.liveConcerns) {
    t9 = summary.liveConcerns.length > 0 && <SummaryList title="Reasons you flagged as real" description="These count against exporting right now. This is where the work is before you commit." reasonIds={summary.liveConcerns} />;
    $[24] = summary.liveConcerns;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== summary.openAssumptions) {
    t10 = summary.openAssumptions.length > 0 && <SummaryList title="Assumptions worth testing" description="You weren't sure here. Cheaper to test from NZ now than to learn in-market after you've committed." reasonIds={summary.openAssumptions} />;
    $[26] = summary.openAssumptions;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== answers.reflection.alternative || $[29] !== answers.reflection.motivation) {
    t11 = (answers.reflection.motivation || answers.reflection.alternative) && <div className="mt-8 space-y-3 border-t border-gray-200 pt-6">{answers.reflection.motivation && <div><p className="text-sm text-gray-500">Why your NZ brand wants to export</p><p className="text-[15px] text-gray-800">{answers.reflection.motivation}</p></div>}{answers.reflection.alternative && <div><p className="text-sm text-gray-500">What the same money and time could do here or in Australia</p><p className="text-[15px] text-gray-800">{answers.reflection.alternative}</p></div>}</div>;
    $[28] = answers.reflection.alternative;
    $[29] = answers.reflection.motivation;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== resolvedExtension) {
    t12 = resolvedExtension && <div className="mt-8">{resolvedExtension}</div>;
    $[31] = resolvedExtension;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== snapshot) {
    t13 = <EnquiryBlock snapshot={snapshot} />;
    $[33] = snapshot;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== onReset) {
    t14 = <button type="button" onClick={onReset} className="rounded-md bg-teal-700 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-teal-800 cursor-pointer">Start over</button>;
    $[35] = onReset;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== onBack) {
    t15 = <button type="button" onClick={onBack} className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer">Back to answers</button>;
    $[37] = onBack;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t14 || $[40] !== t15) {
    t16 = <div className="mt-8 flex items-center gap-4 border-t border-gray-200 pt-6">{t14}{t15}</div>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t10 || $[43] !== t11 || $[44] !== t12 || $[45] !== t13 || $[46] !== t16 || $[47] !== t7 || $[48] !== t8 || $[49] !== t9) {
    t17 = <div className="mx-auto max-w-2xl">{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t16}</div>;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
    $[46] = t16;
    $[47] = t7;
    $[48] = t8;
    $[49] = t9;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  return t17;
}
function SummaryList(t0) {
  const $ = _c(12);
  const {
    title,
    description,
    reasonIds
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-base font-semibold text-gray-900">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = <p className="text-sm text-gray-500">{description}</p>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== reasonIds) {
    t3 = reasonIds.map(_temp);
    $[4] = reasonIds;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <ul className="mt-3 space-y-1.5">{t3}</ul>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <div className="mt-8">{t1}{t2}{t4}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

// --- Enquiry Block ---
function _temp(id) {
  const importance = reasonImportance(id);
  const iconConfig = IMPORTANCE_ICON_CONFIG[importance];
  const Icon = iconConfig.icon;
  return <li key={id} className="flex items-center gap-2 text-[15px] text-gray-800"><Icon className={cn("h-4 w-4 shrink-0", iconConfig.className)} />{reasonTitle(id)}</li>;
}
function EnquiryBlock(t0) {
  const $ = _c(40);
  const {
    snapshot
  } = t0;
  const {
    data: session
  } = useSession();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "",
      email: "",
      message: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [formData, setFormData] = useState(t1);
  const [showSuccess, setShowSuccess] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      mutationFn: _temp2,
      onSuccess: () => {
        setShowSuccess(true);
        toast.success("Your results have been submitted");
      },
      onError: _temp3
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sendEnquiry = useMutation(t2);
  let t3;
  if ($[2] !== formData.email || $[3] !== formData.message || $[4] !== formData.name || $[5] !== sendEnquiry || $[6] !== session?.user?.email || $[7] !== session?.user?.name || $[8] !== snapshot) {
    t3 = function handleSubmit(e) {
      e.preventDefault();
      const name = formData.name || session?.user?.name || "";
      const email = formData.email || session?.user?.email || "";
      if (!name || !email) {
        toast.error("Please fill in your name and email");
        return;
      }
      sendEnquiry.mutate({
        senderName: name,
        senderEmail: email,
        message: formData.message || "I completed the Export Reality Check and would like to discuss my results.",
        toolId: "export-reality-check",
        data: {
          signal: snapshot.summary.signal,
          answeredCount: snapshot.summary.answeredCount,
          applicableCount: snapshot.summary.applicableCount,
          unsureCount: snapshot.summary.unsureCount,
          shouldConcerns: snapshot.summary.shouldConcerns,
          readyConcerns: snapshot.summary.readyConcerns,
          liveConcerns: snapshot.summary.liveConcerns,
          openAssumptions: snapshot.summary.openAssumptions,
          reflection: snapshot.answers.reflection,
          responses: snapshot.answers.responses
        }
      });
    };
    $[2] = formData.email;
    $[3] = formData.message;
    $[4] = formData.name;
    $[5] = sendEnquiry;
    $[6] = session?.user?.email;
    $[7] = session?.user?.name;
    $[8] = snapshot;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleSubmit = t3;
  const nameValue = formData.name || session?.user?.name || "";
  const emailValue = formData.email || session?.user?.email || "";
  if (showSuccess) {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="mt-8 rounded-xl border border-teal-200 bg-teal-50 p-6"><div className="flex items-center gap-3 mb-2"><CheckCircle2 className="h-5 w-5 text-teal-600" /><h3 className="text-base font-semibold text-teal-900">Submitted</h3></div><p className="text-sm text-teal-700">Your results and contact details have been sent. We'll be in touch within a couple of business days.</p></div>;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="bg-gray-950 p-5 sm:p-6"><h3 className="text-base font-semibold text-white">Want to talk through your results?</h3><p className="mt-1 text-sm text-gray-300">Submit your answers and we'll follow up with relevant support, connections, or resources based on where you landed.</p></div>;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <label htmlFor="enquiry-name" className="mb-1 block text-sm font-medium text-gray-700">Name *</label>;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => setFormData(prev => ({
      ...prev,
      name: e_0.target.value
    }));
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== nameValue) {
    t7 = <div>{t5}<input id="enquiry-name" type="text" required={true} value={nameValue} onChange={t6} placeholder="Jane Smith" className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500" /></div>;
    $[14] = nameValue;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <label htmlFor="enquiry-email" className="mb-1 block text-sm font-medium text-gray-700">Email *</label>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e_1 => setFormData(prev_0 => ({
      ...prev_0,
      email: e_1.target.value
    }));
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== emailValue) {
    t10 = <div>{t8}<input id="enquiry-email" type="email" required={true} value={emailValue} onChange={t9} placeholder="jane@yourbrand.co.nz" className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500" /></div>;
    $[18] = emailValue;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t7) {
    t11 = <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{t7}{t10}</div>;
    $[20] = t10;
    $[21] = t7;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <label htmlFor="enquiry-message" className="mb-1 block text-sm font-medium text-gray-700">Anything you'd like to add? (optional)</label>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e_2 => setFormData(prev_1 => ({
      ...prev_1,
      message: e_2.target.value
    }));
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== formData.message) {
    t14 = <div>{t12}<textarea id="enquiry-message" value={formData.message} onChange={t13} placeholder={"Tell us about your brand, what market you're considering, or what you'd like help with\u2026"} rows={3} className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500" /></div>;
    $[25] = formData.message;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = sendEnquiry.isPending;
  const t16 = sendEnquiry.isPending ? "cursor-not-allowed bg-gray-100 text-gray-400" : "bg-teal-700 text-white hover:bg-teal-800";
  let t17;
  if ($[27] !== t16) {
    t17 = cn("flex w-full items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition-all cursor-pointer", t16);
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Send className="h-4 w-4" />;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const t19 = sendEnquiry.isPending ? "Sending\u2026" : "Submit my results";
  let t20;
  if ($[30] !== sendEnquiry.isPending || $[31] !== t17 || $[32] !== t19) {
    t20 = <button type="submit" disabled={t15} className={t17}>{t18}{t19}</button>;
    $[30] = sendEnquiry.isPending;
    $[31] = t17;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <p className="text-center text-xs text-gray-400">Your assessment answers will be included so we can provide relevant follow-up.</p>;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== handleSubmit || $[36] !== t11 || $[37] !== t14 || $[38] !== t20) {
    t22 = <div className="mt-8 overflow-hidden rounded-xl border border-gray-200">{t4}<form onSubmit={handleSubmit} className="space-y-4 bg-white p-5 sm:p-6">{t11}{t14}{t20}{t21}</form></div>;
    $[35] = handleSubmit;
    $[36] = t11;
    $[37] = t14;
    $[38] = t20;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  return t22;
}
function _temp3() {
  toast.error("Failed to send. Please try again.");
}
function _temp2(payload) {
  return authenticatedApiClient.post("tools/tool-enquiry", {
    json: payload
  }).json();
}